import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedClient } from '../../hooks/useSelectedClient';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import Skeleton from 'react-loading-skeleton';
import { Paginator } from 'primereact/paginator';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useCertificateHolders, CertificateHolder } from './hooks/useCertificateHolders';
import ViewCertificateHolder from './components/ViewCertificateHolder';
import AddCertificateHolder, { EditableHolder } from '../send-certificate/components/AddCertificateHolder';
import { formatUsPhone } from '../../utilities/formatPhone';
import '../../styles/certificateHolders.css';
import '../../styles/pages.css';

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
}

const AVATAR_COLORS = ['#D52B1E', '#A200FF', '#1a7a4a', '#A200FF', '#b45309', '#0e7490'];
function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export default function CertificateHoldersPage() {
  const navigate = useNavigate();
  const { insuredId } = useSelectedClient();

  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [showAddHolder, setShowAddHolder] = React.useState(false);
  const [editingHolder, setEditingHolder] = React.useState<EditableHolder | null>(null);
  const [selectedHolder, setSelectedHolder] = React.useState<CertificateHolder | null>(null);
  const [page, setPage] = React.useState(0);
  const pageSize = 10;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { holders, totalCount, isLoading, error, refetch } = useCertificateHolders(
    debouncedQuery, pageSize, page * pageSize
  );

  const handleHolderDialogHide = () => {
    setShowAddHolder(false);
    setEditingHolder(null);
    refetch();
  };

  const renderListView = () => (
    <div className="ch__page">

      {/* ── Header ── */}
      <div className="ch__pageHeader">
        <div className="ch__pageHeaderLeft">
          <button className="ch__backBtn" onClick={() => navigate('/agentdashboard')}>
            <i className="pi pi-arrow-left" />
          </button>
          <div>
            <h1 className="ch__pageTitle">Certificate Holders</h1>
            <p className="ch__pageSubtitle">
              {isLoading ? 'Loading…' : `${totalCount} Certificate holder${totalCount !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <Button
          icon="pi pi-plus"
          label="Add Certificate Holder"
          className="p-button-sm ch__addBtn"
          onClick={() => setShowAddHolder(true)}
        />
      </div>

      {/* ── Search ── */}
      <div className="ch__pageFilters">
        <div className="ch__pageFilterField">
          <label className="ch__pageFilterLabel">Search</label>
          <span className="p-input-icon-left ch__searchWrap">
            <i className="pi pi-search" />
            <InputText
              maxLength={100}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              placeholder="Search by name…"
              className="ch__pageSearchInput"
            />
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="ch__tableWrap">
        {isLoading ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16, borderRadius: 10, overflow: 'hidden', border: '1px solid #edf0f4' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ width: '28%', padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b', borderBottom: '1px solid #edf0f4' }}>Name</th>
                <th style={{ width: '16%', padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b', borderBottom: '1px solid #edf0f4' }}>Location</th>
                <th style={{ width: '24%', padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b', borderBottom: '1px solid #edf0f4' }}>Email</th>
                <th style={{ width: '14%', padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b', borderBottom: '1px solid #edf0f4' }}>Phone</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b', borderBottom: '1px solid #edf0f4' }}>Address</th>
                <th style={{ width: 80, borderBottom: '1px solid #edf0f4' }}></th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: pageSize }).map((_, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f4f6f9', background: '#fff' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Skeleton circle width={34} height={34} />
                      <Skeleton width={120} height={13} borderRadius={4} />
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}><Skeleton width={80} height={13} borderRadius={4} /></td>
                  <td style={{ padding: '12px 16px' }}><Skeleton width={150} height={13} borderRadius={4} /></td>
                  <td style={{ padding: '12px 16px' }}><Skeleton width={90} height={13} borderRadius={4} /></td>
                  <td style={{ padding: '12px 16px' }}><Skeleton width={130} height={13} borderRadius={4} /></td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Skeleton width={28} height={28} borderRadius={6} />
                      <Skeleton width={28} height={28} borderRadius={6} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : error ? (
          <div className="ch__stateBox">
            <i className="pi pi-exclamation-circle" style={{ fontSize: 28, color: '#D52B1E' }} />
            <p>{error}</p>
          </div>
        ) : holders.length === 0 ? (
          <div className="ch__stateBox">
            <i className="pi pi-users" style={{ fontSize: 32, color: '#cbd5e1' }} />
            <p>{searchQuery ? 'No holders match your search.' : 'No certificate holders found.'}</p>
            {!searchQuery && (
              <Button
                label="Add Your First Certificate Holder"
                icon="pi pi-plus"
                className="p-button-sm ch__addBtn"
                style={{ marginTop: 12 }}
                onClick={() => setShowAddHolder(true)}
              />
            )}
          </div>
        ) : (
          <DataTable
            value={holders}
            className="ch__dataTable"
            size="small"
            onRowClick={(e) => setSelectedHolder(e.data as CertificateHolder)}
            rowClassName={() => 'ch__dataRow'}
          >
            {/* Name + avatar */}
            <Column
              header="Name"
              style={{ width: '28%' }}
              body={(r: CertificateHolder) => (
                <div className="ch__nameCell">
                  <span
                    className="ch__tableAvatar"
                    style={{ background: avatarColor(r.name || '') }}
                  >
                    {getInitials(r.name)}
                  </span>
                  <span className="ch__tablePrimary">{r.name || '—'}</span>
                </div>
              )}
            />

            {/* Location */}
            <Column
              header="Location"
              style={{ width: '16%' }}
              body={(r: CertificateHolder) => {
                const loc = [r.city, r.stateName].filter(Boolean).join(', ');
                return <span className="ch__tableSecondary">{loc || '—'}</span>;
              }}
            />

            {/* Email */}
            <Column
              header="Email"
              style={{ width: '24%' }}
              body={(r: CertificateHolder) => (
                <span className="ch__tableSecondary ch__tableSecondary--truncate" title={r.eMail}>
                  {r.eMail || '—'}
                </span>
              )}
            />

            {/* Phone */}
            <Column
              header="Phone"
              style={{ width: '14%' }}
              body={(r: CertificateHolder) => {
                const ph = r.phone || r.cellPhone || '';
                return <span className="ch__tableSecondary">{formatUsPhone(ph) || '—'}</span>;
              }}
            />

            {/* Address */}
            <Column
              header="Address"
              body={(r: CertificateHolder) => (
                <span className="ch__tableSecondary ch__tableSecondary--truncate" title={r.addressLine1}>
                  {r.addressLine1 || '—'}
                </span>
              )}
            />

            {/* Actions */}
            <Column
              header=""
              style={{ width: '80px' }}
              body={(r: CertificateHolder) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                  <button
                    className="emailTpl__actionBtn emailTpl__actionBtn--edit"
                    title="Edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingHolder(r);
                      setShowAddHolder(true);
                    }}
                  >
                    <i className="pi pi-pencil" />
                  </button>
                  <i className="pi pi-chevron-right ch__tableChevron" />
                </div>
              )}
            />
          </DataTable>
        )}
      </div>

      {/* ── Paginator ── */}
      {!isLoading && !error && totalCount > pageSize && (
        <div className="ch__paginator">
          <Paginator
            rows={pageSize}
            totalRecords={totalCount}
            first={page * pageSize}
            onPageChange={(e) => setPage(Math.floor(e.first / pageSize))}
            template="PrevPageLink PageLinks NextPageLink"
            className="requests-paginator"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="page certHoldersPage">
      <div className="ch__container">
        {selectedHolder ? (
          <ViewCertificateHolder
            holder={selectedHolder}
            onBackToList={() => setSelectedHolder(null)}
          />
        ) : (
          renderListView()
        )}
      </div>

      <AddCertificateHolder
        visible={showAddHolder}
        onHide={handleHolderDialogHide}
        insuredDatabaseId={insuredId}
        editHolder={editingHolder}
      />
    </div>
  );
}
